import { useMemo } from 'react';
import Select, {
  components,
  GroupBase,
  StylesConfig,
  ThemeConfig,
} from 'react-select';
import CreatableSelect from 'react-select/creatable';

import { useUISettings } from '../context/uiSettingsContext';
import type { CustomDropdownProps, OptionType } from '../model';

const useTheme =
  (isDark: boolean): ThemeConfig =>
  (theme) => ({
    ...theme,
    colors: {
      ...theme.colors,
      primary: 'var(--color-brand-600)',
      primary25: isDark ? '#374151' : '#f3f4f6',
      neutral0: isDark ? '#111827' : '#ffffff',
      neutral80: isDark ? '#f9fafb' : '#111827',
    },
  });

const useStyles = (
  isDark: boolean,
  isDisabled?: boolean,
  formMode?: boolean,
  hasError?: boolean
): StylesConfig<OptionType, boolean, GroupBase<OptionType>> => {
  const CURSOR = { DISABLED: 'not-allowed', ENABLED: 'pointer' };

  return {
    control: (base, state) => {
      let backgroundColor;
      if (isDisabled) {
        backgroundColor = isDark ? '#2e2e2e' : '#e5e7eb';
      } else if (hasError) {
        backgroundColor = isDark ? '#450a0a' : '#fef2f2';
      } else {
        backgroundColor = isDark ? '#111827' : '#fff';
      }

      let borderColor;
      if (isDisabled) {
        borderColor = '#9ca3af';
      } else if (hasError) {
        borderColor = '#ef4444';
      } else if (state.isFocused) {
        borderColor = 'var(--color-brand-600)';
      } else {
        borderColor = '#d1d5db';
      }
      return {
        ...base,
        width: formMode ? 163 : undefined,
        backgroundColor,
        borderColor,
        boxShadow:
          state.isFocused && !isDisabled
            ? `0 0 0 1px ${hasError ? '#ef4444' : 'var(--color-brand-600)'}`
            : 'none',
        opacity: isDisabled ? 0.6 : 1,
        cursor: isDisabled ? 'not-allowed' : 'default',
        '&:hover': {
          borderColor: isDisabled
            ? '#9ca3af'
            : hasError
            ? '#ef4444'
            : 'var(--color-brand-600)',
        },
        color: isDark ? '#fff' : '#111827',
        marginTop: 5,
      };
    },
    singleValue: (base) => ({ ...base, color: isDark ? '#ffffff' : '#111827' }),
    menu: (base) => ({
      ...base,
      backgroundColor: isDark ? '#1f2937' : '#ffffff',
      zIndex: 20,
    }),
    menuList: (base) => ({
      ...base,
      backgroundColor: 'inherit',
      maxHeight: 150,
      overflowY: 'auto',
    }),
    option: (base, state) => {
      let backgroundColor;
      if (state.isFocused) {
        backgroundColor = isDark ? '#374151' : '#e5e7eb';
      } else {
        backgroundColor = 'inherit';
      }
      const color = isDark ? '#f9fafb' : '#111827';
      const cursor = isDisabled ? CURSOR.DISABLED : CURSOR.ENABLED;
      return {
        ...base,
        backgroundColor,
        color,
        cursor,
      };
    },
    multiValue: (base, state) => ({
      ...base,
      backgroundColor: isDark
        ? 'rgba(var(--color-brand-900-rgb, 38, 46, 137), 0.2)'
        : 'var(--color-brand-50)',
      borderRadius: 8,
      paddingLeft: 4,
      paddingRight: (state.data as any).isFixed ? 4 : 0,
      border: `1px solid ${
        isDark
          ? 'rgba(var(--color-brand-500-rgb, 70, 95, 255), 0.2)'
          : 'var(--color-brand-100)'
      }`,
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: isDark ? 'var(--color-brand-300)' : 'var(--color-brand-700)',
      fontSize: '12px',
      fontWeight: 600,
      padding: '2px 6px',
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: isDark ? 'var(--color-brand-300)' : 'var(--color-brand-700)',
      cursor: 'pointer',
      borderRadius: 4,
      marginLeft: 2,
      ':hover': {
        backgroundColor: isDark ? '#ef444433' : '#fee2e2',
        color: '#ef4444',
      },
    }),
    clearIndicator: (base) => ({
      ...base,
      color: '#dc2626',
      cursor: 'pointer',
      ':hover': { color: '#b91c1c' },
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: '#6b7280',
      cursor: isDisabled ? CURSOR.DISABLED : CURSOR.ENABLED,
      ':hover': { color: '#374151' },
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
  };
};

const MultiValueRemove = (props: any) => {
  if (props.data.isFixed) {
    return null;
  }
  return <components.MultiValueRemove {...props} />;
};

const Dropdown = ({
  label,
  value,
  setValue,
  options = [],
  setOptions,
  required = false,
  placeholder = 'Select...',
  isSearchable = false,
  isCreatable = false,
  labelKey = 'label',
  valueKey = 'value',
  isDisabled,
  formMode = false,
  isClearAllowed = false,
  isFocusAllowed = false,
  isMulti = false,
  error,
  appendTo,
  id,
}: CustomDropdownProps) => {
  const { themeMode } = useUISettings();
  const isDark = themeMode === 'dark';

  const mappedOptions = options.map((opt) => ({
    label: opt[labelKey],
    value: opt[valueKey],
    isFixed: opt.isFixed,
  }));

  const handleCreate = (inputValue: string) => {
    const newOption = {
      [labelKey]: inputValue,
      [valueKey]: inputValue,
    };
    if (setOptions && !options.some((opt) => opt[valueKey] === inputValue)) {
      setOptions((prev: Record<string, any>[]) => [...prev, newOption]);
    }
  };

  const filterOption = (
    option: { label: string; value: string },
    inputValue: string
  ) => (option.label ?? '').toLowerCase().includes(inputValue.toLowerCase());

  const formatCreateLabel = (inputValue: string) => `Add item "${inputValue}"`;

  const SelectComponent = isCreatable ? CreatableSelect : Select;

  const finalPlaceholder =
    formMode && placeholder.length > 10
      ? placeholder.slice(0, 10) + '...'
      : placeholder;

  const selectedSingleValue = useMemo(() => {
    if (value !== null && value !== undefined && value !== '') {
      return (
        mappedOptions.find((opt) => String(opt.value) === String(value)) || null
      );
    }
    return null;
  }, [value, mappedOptions]);

  const selectedMultiValue = useMemo(() => {
    return Array.isArray(value)
      ? mappedOptions.filter((opt) => value.includes(opt.value))
      : [];
  }, [value, mappedOptions]);

  const styles = useStyles(isDark, isDisabled, formMode, !!error);
  const theme = useTheme(isDark);

  const handleMultiChange = (selected: any, actionMeta: any) => {
    switch (actionMeta.action) {
      case 'remove-value':
      case 'pop-value':
        if (actionMeta.removedValue.isFixed) {
          return;
        }
        break;
      case 'clear':
        selected = mappedOptions.filter((v) => v.isFixed);
        break;
    }

    const values = Array.isArray(selected)
      ? selected.map((opt: OptionType) => opt.value)
      : [];
    setValue(values);
  };

  const handleSingleChange = (selected: unknown) => {
    setValue((selected as OptionType | null)?.value || '');
  };

  return (
    <div>
      {label && (
        <label className="label">
          {label} {required && <span className="text-error-600">*</span>}
        </label>
      )}

      <SelectComponent
        isMulti={isMulti}
        isClearable={
          isMulti
            ? isClearAllowed && Array.isArray(value) && value.length > 0
            : isClearAllowed && !!value
        }
        isSearchable={isSearchable}
        placeholder={finalPlaceholder}
        options={mappedOptions}
        inputId={id}
        value={isMulti ? selectedMultiValue : selectedSingleValue}
        onChange={isMulti ? (handleMultiChange as any) : handleSingleChange}
        components={{
          MultiValueRemove,
        }}
        onCreateOption={
          isCreatable
            ? (inputValue) => {
                handleCreate(inputValue);
                if (isMulti) {
                  setValue([
                    ...(Array.isArray(value) ? value : []),
                    inputValue,
                  ]);
                } else {
                  setValue(inputValue);
                }
              }
            : undefined
        }
        formatCreateLabel={isCreatable ? formatCreateLabel : undefined}
        filterOption={filterOption}
        className="react-select-container"
        classNamePrefix="react-select"
        styles={styles}
        theme={theme}
        isDisabled={isDisabled}
        menuPlacement="auto"
        autoFocus={isFocusAllowed}
        menuPortalTarget={
          appendTo === 'body'
            ? document.body
            : appendTo
            ? (document.querySelector(appendTo) as HTMLElement)
            : null
        }
      />
      {error && (
        <p className="error-msg mt-1">
          {typeof error === 'string' ? error : error.message}
        </p>
      )}
    </div>
  );
};

export default Dropdown;
